"use client";

import React, { useMemo, useState, useEffect } from "react";
import { Button } from "@os/components/ui/button";
import { Input } from "@os/components/ui/input";
import { Textarea } from "@os/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@os/components/ui/dialog";
import { useToast } from "@os/components/ui/use-toast";
import { Sparkles } from "lucide-react";
import { sendMessageToModel } from "@/services/ModelService";
import { AIModel } from "@/types";
import { useSupabaseStore } from "@/store/SupabaseStore";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@os/components/ui/select";
import { Switch } from "@os/components/ui/switch";
import { Label } from "@os/components/ui/label";
import { generateImage } from "@/lib/openai";

type Props = {
  open: boolean;
  onClose: () => void;
  onApply: (mode: "replace" | "append", mdDeck: string) => void;
};

function extractMarkdown(text: string): string {
  // Find all fenced code blocks tagged as md/markdown and pick the best candidate
  const fenceRe = /```(?:md|markdown)?\s*([\s\S]*?)```/gi;
  const candidates: string[] = [];
  let m: RegExpExecArray | null;
  while ((m = fenceRe.exec(text)) !== null) {
    candidates.push((m[1] || "").trim());
  }

  if (candidates.length > 0) {
    // Prefer a candidate containing '---' separators or multiple headings
    const score = (s: string) => {
      const sep = (s.match(/\n-{3,}\n/g) || []).length;
      const h1 = (s.match(/^\s*#\s+/gm) || []).length;
      const h2 = (s.match(/^\s*##\s+/gm) || []).length;
      return sep * 5 + (h1 + h2) * 2 + s.length / 1000;
    };
    candidates.sort((a, b) => score(b) - score(a));
    return candidates[0];
  }

  // Otherwise, return plain text as-is for downstream normalization
  return text.trim();
}

// NEW: ensure the AI output is a proper deck with --- separators and per-slide directives
function normalizeDeck(md: string, theme: ThemePreset): string {
  let body = md.trim();

  // Strip stray code fences if the model kept them
  body = body.replace(/^```(?:md|markdown)?\s*/i, "").replace(/```$/i, "").trim();

  // PRE-CLEAN: normalize common "concept deck" patterns from chatty outputs

  // A) Convert bullet-embedded separators into real separators + next slide titles
  body = body.replace(
    /^\s*-\s*---\s*(?:#{1,6}\s*)?(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*(.+?)\s*(?:\*\*)?\s*$/gim,
    (_m, t) => `\n\n---\n\n# ${t.trim()}`
  );
  body = body.replace(
    /^\s*-\s*---\s*(#{1,6}\s+.+)\s*$/gim,
    (_m, h) => `\n\n---\n\n${h.trim()}`
  );
  body = body.replace(
    /^\s*-\s*---\s*(.+)\s*$/gim,
    (_m, rest) => `\n\n---\n\n${rest.trim()}`
  );
  body = body.replace(/^\s*-\s*---\s*$/gim, "---");

  // B) Handle '--- ## Slide N — Title' or '--- ## Heading' on the same line
  body = body.replace(
    /^\s*---\s*(?:#{1,6}\s*)?(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*(.+?)\s*(?:\*\*)?\s*$/gim,
    (_m, t) => `\n\n---\n\n# ${t.trim()}`
  );
  body = body.replace(
    /^\s*---\s*(#{1,6}\s+.+)\s*$/gim,
    (_m, h) => `\n\n---\n\n${h.trim()}`
  );

  // NEW: Fix compact separator-heading forms like '---# Title' or '---## Heading'
  body = body.replace(
    /^\s*-\s*---\s*#\s*(.+)\s*$/gim,
    (_m, t) => `\n\n---\n\n# ${t.trim()}`
  );
  body = body.replace(
    /^\s*-\s*---\s*(#{1,6})\s*(.+)\s*$/gim,
    (_m, _hashes, title) => `\n\n---\n\n# ${String(title).trim()}`
  );
  body = body.replace(
    /^\s*---\s*#\s*(.+)\s*$/gim,
    (_m, t) => `\n\n---\n\n# ${t.trim()}`
  );
  body = body.replace(
    /^\s*---\s*(#{1,6})\s*(.+)\s*$/gim,
    (_m, _hashes, title) => `\n\n---\n\n# ${String(title).trim()}`
  );

  // 1) Convert accidental list-based separators into real separators
  body = body.replace(/^\s*-\s*---\s*$/gim, "---");

  // 2) Normalize "Slide N — Title" lines into "# Title"
  body = body
    .replace(/^\s{0,3}#{1,6}\s+(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*(.+?)(?:\*\*)?\s*$/gim, (_m, t) => `# ${t.trim()}`)
    .replace(/^\s*(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*(.+?)(?:\*\*)?\s*$/gim, (_m, t) => `# ${t.trim()}`)
    .replace(/^\s{0,3}(#{1,6}\s+)\*\*(.+?)\*\*\s*$/gim, (_m, hashes, t) => `${hashes}${t.trim()}`);

  // 3) If there is a heading anywhere, drop any leading preamble before it
  const firstHeadingIdx = body.search(/^\s*(?:#{1,6}\s+|Slide\s+\d+\s*(?:—|-|:))/m);
  if (firstHeadingIdx > 0) {
    body = body.slice(firstHeadingIdx);
  }

  // Check for proper separators
  const hasSeparators = /\n-{3,}\n/.test(body);
  let slides: string[] = [];

  if (hasSeparators) {
    slides = body.replace(/\r\n/g, "\n").split(/\n-{3,}\n/g).map(s => s.trim()).filter(Boolean);
  } else {
    // Fallback: split on heading starts OR "Slide N — Title" patterns
    const lines = body.replace(/\r\n/g, "\n").split("\n");
    const out: string[] = [];
    let current: string[] = [];

    const slideTitleRe = /^(?:#{1,6}\s+.*|(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*.*)$/i;
    const isHeading = (ln: string) => slideTitleRe.test(ln.trim());

    const toCleanH1 = (ln: string) => {
      let s = ln.trim();
      s = s
        .replace(/^\s*#{1,6}\s+/, "")
        .replace(/^\s*(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*/i, "")
        .replace(/^\*\*(.+)\*\*$/i, "$1")
        .trim();
      return `# ${s}`;
    };

    lines.forEach((ln) => {
      if (isHeading(ln)) {
        if (current.length) out.push(current.join("\n").trim());
        current = [toCleanH1(ln)];
      } else {
        current.push(ln);
      }
    });
    if (current.length) out.push(current.join("\n").trim());

    slides = out.filter(Boolean);
    if (slides.length === 0) {
      slides = [body];
    }
  }

  // Sanitize each slide: remove stray '---' lines and italic-only meta lines like "(8 core slides …)"
  const sanitizeSlide = (s: string) => {
    const lines = s.split("\n");
    const cleaned = lines
      .filter(l => !/^\s*-{3,}\s*$/.test(l)) // remove lone horizontal rules
      .filter(l => !/^\s*\*?\(?.*slides?.*\)?\*?\s*$/i.test(l.trim())) // drop meta lines
      .map(l => l);
    return cleaned.join("\n").trim();
  };
  slides = slides.map(sanitizeSlide);

  // NEW: resplit slides if inline breaks like "- --- …" or "--- …" remain inside a slide
  const resplitOnInlineBreaks = (arr: string[]): string[] => {
    const out: string[] = [];
    const cleanHeading = (txt: string) => {
      return (
        "# " +
        txt
          .replace(/^\s*(#{1,6}\s+)?(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*/i, "")
          .replace(/^\s*#{1,6}\s+/, "")
          .replace(/^\*\*(.+)\*\*$/, "$1")
          .trim()
      );
    };

    arr.forEach((s) => {
      const lines = s.split("\n");
      let buf: string[] = [];
      const flush = () => {
        const chunk = buf.join("\n").trim();
        if (chunk) out.push(chunk);
        buf = [];
      };

      lines.forEach((ln) => {
        // First, convert "## Slide N — Title" at line start to clean H1
        let line = ln.replace(
          /^\s*#{1,6}\s+(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*(.+)\s*$/i,
          (_m, t) => `# ${String(t).trim()}`
        );

        // If the line contains any '---' anywhere, split into segments
        if (line.includes('---')) {
          const parts = line.split(/---/g);
          // Part before first '---' stays in current slide
          if (parts[0].trim()) {
            buf.push(parts[0]);
          }
          // Each subsequent part starts a new slide; convert to heading if needed
          for (let i = 1; i < parts.length; i++) {
            flush(); // end current slide at each '---'
            const rest = (parts[i] || '').trim();
            if (!rest) continue;
            if (/^\s*(#{1,6}\s+)?(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*/i.test(rest)) {
              buf.push(cleanHeading(rest));
            } else if (/^\s*#{1,6}\s+/.test(rest)) {
              const title = rest.replace(/^\s*#{1,6}\s+/, "").trim();
              buf.push(`# ${title}`);
            } else {
              buf.push(`# ${rest}`);
            }
          }
        } else if (/^\s*-\s*---\s*(.*)$/.test(line)) {
          // Bullet-prefixed separator at start of line
          const mBullet = line.match(/^\s*-\s*---\s*(.*)$/);
          flush();
          const rest = (mBullet?.[1] || '').trim();
          if (rest) {
            if (/^\s*(#{1,6}\s+)?(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*/i.test(rest)) {
              buf.push(cleanHeading(rest));
            } else if (/^\s*#{1,6}\s+/.test(rest)) {
              const title = rest.replace(/^\s*#{1,6}\s+/, "").trim();
              buf.push(`# ${title}`);
            } else {
              buf.push(`# ${rest}`);
            }
          }
        } else if (/^\s*---\s*(.*)$/.test(line)) {
          // Plain separator at start of line
          const mInline = line.match(/^\s*---\s*(.*)$/);
          flush();
          const rest = (mInline?.[1] || '').trim();
          if (rest) {
            if (/^\s*(#{1,6}\s+)?(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*/i.test(rest)) {
              buf.push(cleanHeading(rest));
            } else if (/^\s*#{1,6}\s+/.test(rest)) {
              const title = rest.replace(/^\s*#{1,6}\s+/, "").trim();
              buf.push(`# ${title}`);
            } else {
              buf.push(`# ${rest}`);
            }
          }
        } else {
          buf.push(line);
        }
      });

      flush();
    });

    return out;
  };

  slides = resplitOnInlineBreaks(slides);

  // NEW: strip decorative icons/emoji from headings/bullets while preserving content
  const cleanLine = (l: string) => {
    const trimmed = l.trim();
    if (trimmed.startsWith("::")) return l; // keep directives
    let s = l;
    // Remove emoji/dingbats/flags/variation selectors
    s = s.replace(/[\u{2700}-\u{27BF}\u{1F300}-\u{1FAFF}\u{1F1E6}-\u{1F1FF}\u{FE0F}]/gu, "");
    // Remove common decorative markers at line start (keeps bullets like '-' intact)
    s = s.replace(/^\s*(?:[•◦●▪▫▸▹►▶◆◇★☆✓✔✦✳❗❌–—→⇒»›])+(\s+)?/u, "");
    // Normalize multiple spaces
    s = s.replace(/\s{2,}/g, " ");
    return s;
  };

  slides = slides.map(s => s.split("\n").map(cleanLine).join("\n").trim());

  // Ensure directives on top of each slide
  const withDirectives = slides.map((s) => {
    const t = s.trim();
    const lns = t.split("\n");
    const firstIdx = lns.findIndex(l => l.trim().length > 0);
    const hasDirective = firstIdx >= 0 && lns[firstIdx].trim().startsWith("::");

    if (hasDirective) {
      const joined = lns.join("\n");
      const hasBg = /(^|\n)::\s*bg\s*=/.test(joined);
      const hasText = /(^|\n)::\s*text\s*=/.test(joined);
      let prepend = "";
      if (!hasBg) prepend += `::bg=${theme.bg}\n`;
      if (!hasText) prepend += `::text=${theme.text}\n`;
      return prepend ? `${prepend}${t}` : t;
    }

    return `::bg=${theme.bg}\n::text=${theme.text}\n${t}`;
  });

  return withDirectives.map(x => x.trim()).join("\n\n---\n\n");
}

// NEW: Build a multi-slide deck from raw notes if the model output isn't structured
function buildFallbackDeckFromNotes(
  notes: string,
  theme: ThemePreset,
  fmt: FormatKey,
  density: "minimal" | "concise" | "detailed" | "extensive"
): string {
  const outline = outlineForFormat(fmt);
  const targetSlidesMap: Record<typeof density, number> = {
    minimal: 6,
    concise: 8,
    detailed: 12,
    extensive: 16,
  };
  const bulletsPerSlideMap: Record<typeof density, number> = {
    minimal: 2,
    concise: 3,
    detailed: 4,
    extensive: 5,
  };

  const targetSlides = targetSlidesMap[density] || 8;
  const bulletsPerSlide = bulletsPerSlideMap[density] || 3;

  // Extract simple bullets from notes: split into sentences
  const cleaned = notes.replace(/\r\n/g, " ").replace(/\n/g, " ").replace(/\s+/g, " ").trim();
  const sentences = cleaned.split(/(?<=[.!?])\s+/).filter((s) => s && s.trim().length > 0);

  const slides: string[] = [];
  const total = Math.min(targetSlides, Math.max(outline.length, 3)); // ensure at least a few slides

  let sentenceIdx = 0;
  for (let i = 0; i < total; i++) {
    const title = outline[i] || `Section ${i + 1}`;
    const bullets: string[] = [];

    for (let b = 0; b < bulletsPerSlide; b++) {
      const s = sentences[sentenceIdx % Math.max(1, sentences.length)] || "";
      sentenceIdx++;
      const bullet = s ? s.trim().slice(0, 180) : "Key point based on your notes.";
      bullets.push(`- ${bullet}`);
    }

    const slideMd = [
      `::bg=${theme.bg}`,
      `::text=${theme.text}`,
      `# ${title}`,
      "",
      ...bullets,
    ].join("\n");

    slides.push(slideMd.trim());
  }

  // Ensure a sensible first slide
  if (slides.length > 0) {
    const firstLines = slides[0].split("\n");
    const hasH1 = firstLines.some((ln) => ln.trim().startsWith("# "));
    if (!hasH1) {
      slides[0] = [`::bg=${theme.bg}`, `::text=${theme.text}`, `# ${outline[0] || "Title"}`, "", "- Generated from your notes"].join("\n");
    }
  }

  return slides.join("\n\n---\n\n");
}

type ThemePreset = { label: string; bg: string; text: string; note?: string };
const THEME_PRESETS: Record<string, ThemePreset> = {
  "canxp-light": { label: "CanXP Maple (Light)", bg: "#F6F9FF", text: "#111827", note: "clean maple gradient / subtle leaf watermark" },
  "canxp-dark": { label: "CanXP Maple (Dark)", bg: "#0B1220", text: "#E5E7EB", note: "dark canvas, high contrast" },
  "corporate-blue": { label: "Corporate Blue", bg: "#F3F7FB", text: "#0F172A" },
  "minimal": { label: "Minimal", bg: "#FFFFFF", text: "#0F172A" },
};

type FormatKey = "pitch" | "strategy" | "report" | "product" | "training";
const FORMAT_LABELS: Record<FormatKey, string> = {
  pitch: "Pitch Deck",
  strategy: "Strategy Proposal",
  report: "Executive Report",
  product: "Product Demo",
  training: "Training/Workshop",
};

function outlineForFormat(fmt: FormatKey): string[] {
  switch (fmt) {
    case "pitch":
      return [
        "Title",
        "The Moment / Problem",
        "What We Built",
        "Why This Matters",
        "How It Works (Architecture or Flow)",
        "Proof / Traction",
        "Roadmap",
        "Call to Action",
      ];
    case "strategy":
      return [
        "Title",
        "Context & Objectives",
        "Current State",
        "Strategic Pillars",
        "Initiatives & Timeline",
        "Risks & Mitigations",
        "Metrics & Governance",
        "Next Steps",
      ];
    case "report":
      return [
        "Title",
        "Executive Summary",
        "Key Findings",
        "Data & Methodology",
        "Implications",
        "Recommendations",
        "Appendix / References",
      ];
    case "product":
      return [
        "Title",
        "Problem & Audience",
        "Solution Overview",
        "Key Features",
        "Demo Flow (1–2 slides)",
        "Architecture / Integrations",
        "Pricing & Plans",
        "CTA",
      ];
    case "training":
      return [
        "Title",
        "Agenda & Outcomes",
        "Module 1 (Concepts)",
        "Module 2 (Hands‑on)",
        "Best Practices",
        "Q&A / Resources",
        "Next Steps",
      ];
    default:
      return ["Title", "Agenda", "Content", "Conclusion"];
  }
}

const MapleSlideGenerator: React.FC<Props> = ({ open, onClose, onApply }) => {
  const { toast } = useToast();
  const { models, fetchModels } = useSupabaseStore();

  useEffect(() => {
    if (!Array.isArray(models) || models.length === 0) {
      fetchModels().catch(() => {});
    }
  }, [models, fetchModels]);

  const model: AIModel | null = useMemo(() => {
    const list = Array.isArray(models) ? models : [];
    const byProvider = (p: string) => list.find((m) => m.provider === p && m.is_active !== false);
    return byProvider("maplept") || byProvider("openai") || list.find((m) => m.is_active !== false) || null;
  }, [models]);

  // User inputs
  const [input, setInput] = useState("");
  const [mode, setMode] = useState<"replace" | "append">("replace");
  const [loading, setLoading] = useState(false);

  // New controls
  const [format, setFormat] = useState<FormatKey>("pitch");
  const [themeKey, setThemeKey] = useState<string>("canxp-light");
  const [density, setDensity] = useState<"minimal" | "concise" | "detailed" | "extensive">("concise");
  const [audience, setAudience] = useState<string>("Government officials, enterprise leaders, innovation agencies, investors, and tech executives (Canadian English)");
  const [tone, setTone] = useState<string>("Confident, authoritative, concise");
  const [twoColumn, setTwoColumn] = useState<boolean>(false);
  const [visualNotes, setVisualNotes] = useState<boolean>(true);
  const [advancedMode, setAdvancedMode] = useState<boolean>(true);
  const [autoImages, setAutoImages] = useState<boolean>(true); // NEW: toggle to generate images for "Visual:" lines

  // NEW: local preprocessing to clean notes before prompting the model
  function preprocessNotes(notes: string): string {
    let text = (notes || "").replace(/\r\n/g, "\n");

    // Strip emoji/dingbats/flags/variation selectors
    text = text.replace(/[\u{2700}-\u{27BF}\u{1F300}-\u{1FAFF}\u{1F1E6}-\u{1F1FF}\u{FE0F}]/gu, "");

    // Remove decorative markers at line start (keep bullets like '-' intact)
    text = text.split("\n").map(ln =>
      ln.replace(/^\s*(?:[•◦●▪▫▸▹►▶◆◇★☆✓✔✦✳❗❌–—→⇒»›])+(\s+)?/u, "")
    ).join("\n");

    // Normalize compact separators and headings e.g., '---# Title' / '---## Heading' / '- --- ## Heading'
    text = text.replace(/^\s*-\s*---\s*#\s*(.+)\s*$/gim, "\n\n---\n\n# $1");
    text = text.replace(/^\s*-\s*---\s*(#{1,6})\s*(.+)\s*$/gim, (_, __, t) => `\n\n---\n\n# ${String(t).trim()}`);
    text = text.replace(/^\s*---\s*#\s*(.+)\s*$/gim, "\n\n---\n\n# $1");
    text = text.replace(/^\s*---\s*(#{1,6})\s*(.+)\s*$/gim, (_, __, t) => `\n\n---\n\n# ${String(t).trim()}`);

    // Convert 'Slide N —|-|: Title' into clean H1
    text = text.replace(/^\s*(?:\*\*)?\s*Slide\s+\d+\s*(?:—|-|:)\s*(.+?)(?:\*\*)?\s*$/gim, "# $1");

    // Collapse excessive spaces
    text = text.replace(/[ \t]{2,}/g, " ");

    return text.trim();
  }

  const buildPrompt = () => {
    const theme = THEME_PRESETS[themeKey] || THEME_PRESETS["minimal"];
    const outline = outlineForFormat(format);

    const slideCountHint =
      density === "minimal" ? "6–8 slides" :
      density === "concise" ? "8–12 slides" :
      density === "detailed" ? "12–16 slides" : "16–20 slides";

    const layoutTip = twoColumn
      ? `When a slide benefits from two columns, add '::layout=two-column' at the very top and split content with a line containing only '||'.`
      : `Use single-column by default. You MAY add '::layout=two-column' if it improves clarity, using '||' to split columns.`;

    const visualsTip = visualNotes
      ? `At the end of slides where helpful, include a line starting with 'Visual:' suggesting a simple diagram, chart, or image (no external links).`
      : `Do not include any visual suggestions; text only.`;

    const skeleton = outline
      .map((title, idx) => {
        if (idx === 0) {
          return [
            "# " + (title || "Title"),
            "",
            "- Optional subtitle or tagline",
          ].join("\n");
        }
        return [
          `# ${title}`,
          "",
          "- Key point 1",
          "- Key point 2",
          "- Key point 3",
        ].join("\n");
      })
      .join("\n\n---\n\n");

    const userNotes = advancedMode ? preprocessNotes(input) : input;

    const guidance = [
      "You are a presentation assistant. Generate a clean, high-signal slide deck in pure Markdown.",
      "",
      `Audience: ${audience}`,
      `Tone: ${tone}`,
      `Format: ${FORMAT_LABELS[format]} (${slideCountHint})`,
      "",
      "Output rules (STRICT):",
      "- Return ONLY a single fenced code block labeled 'markdown' that contains the deck. Do not include any text before or after it.",
      "- Each slide starts with a top-level heading: '# Title'. No icons/emojis or decorative symbols in titles or bullets.",
      "- Separate slides with a line containing exactly three dashes: ---",
      "- Add theme directives on the top of EVERY slide:",
      `  ::bg=${theme.bg}`,
      `  ::text=${theme.text}`,
      `- ${layoutTip}`,
      "- If using two columns, put left content first, then a line with '||', then right content.",
      "- Keep bullets concise and executive-friendly.",
      "- Do NOT output compact artifacts like '---# Title'; always use a separator line followed by '# Title' on the next line.",
      "- Do NOT include disclaimers, boilerplate, or non-slide text outside the fenced block.",
      visualsTip,
      "",
      "Skeleton (for structure guidance only; do not echo verbatim):",
      "```markdown",
      skeleton,
      "```",
      "",
      "Use the user's notes (pre-cleaned if Advanced is enabled) to produce the final deck:",
      userNotes || "(No additional notes provided)",
      "",
      "Respond with a single fenced block like this:",
      "```markdown",
      "::bg=#FFFFFF",
      "::text=#0F172A",
      "# Title",
      "- Bullet",
      "",
      "---",
      "",
      "# Next Slide",
      "- Bullet",
      "```",
    ];

    return guidance.join("\n");
  };

  // NEW: generate images for "Visual:" lines and embed them
  async function injectGeneratedImages(mdDeck: string): Promise<string> {
    const lines = mdDeck.split("\n");
    const cache = new Map<string, string>();
    const out: string[] = [];

    for (const ln of lines) {
      const m = ln.match(/^\s*Visual:\s*(.+)$/i);
      if (!m) {
        out.push(ln);
        continue;
      }
      const prompt = m[1].trim();
      // Generate or reuse cached image
      let url = cache.get(prompt);
      if (!url) {
        url = await generateImage(prompt);
        cache.set(prompt, url);
      }
      // Replace the "Visual:" line with a responsive image block
      const imgHtml = `<div style='margin-top:0.5rem'><img src="${url}" alt="${prompt}" style='width:100%;border-radius:0.5rem'/></div>`;
      out.push(imgHtml);
    }

    return out.join("\n");
  }

  const generate = async () => {
    if (!input.trim()) {
      toast({ title: "Paste content first", description: "Add notes or outline to generate slides.", variant: "destructive" });
      return;
    }
    if (!model) {
      toast({ title: "No model available", description: "Please configure an AI model in Settings.", variant: "destructive" });
      return;
    }

    // Helper: pick a backup model from available providers
    const chooseBackupModel = (current: AIModel | null): AIModel | null => {
      const list = (Array.isArray(models) ? models : []).filter(m => m.is_active !== false);
      const preference = ["openai", "gemini", "deepseek", "cohere", "maplept", "ollama"];
      for (const prov of preference) {
        const candidate = list.find(m => m.provider === prov && (!current || m.id !== current.id));
        if (candidate) return candidate;
      }
      return null;
    };

    // Helper: decide if response is not a usable deck
    const isLikelyBadResponse = (txt: string) => {
      const t = (txt || "").trim();
      if (!t) return true;
      const lower = t.toLowerCase();
      if (lower.startsWith("error:") || lower.includes("unable to connect") || lower.includes("temporarily unavailable")) return true;
      // No headings and no separators → not a deck
      if (!/(^|\n)\s*#\s+/.test(t) && !/(^|\n)\s*---\s*(\n|$)/.test(t)) return true;
      return false;
    };

    setLoading(true);
    try {
      // First attempt with selected model
      let response = await sendMessageToModel(
        [{ id: "gen", chatId: "mapleslide", role: "user", sender: "user", timestamp: new Date().toISOString(), content: buildPrompt() }],
        model,
        Array.isArray(models) ? models : []
      );

      let content = response.content || "";
      let mdRaw = extractMarkdown(content);

      // Retry with backup model if the first response was not usable
      if (isLikelyBadResponse(content) || !mdRaw) {
        const backup = chooseBackupModel(model);
        if (backup) {
          toast({ title: "Switching model", description: `Retrying with ${backup.name} for better results…` });
          const retry = await sendMessageToModel(
            [{ id: "gen2", chatId: "mapleslide", role: "user", sender: "user", timestamp: new Date().toISOString(), content: buildPrompt() }],
            backup,
            Array.isArray(models) ? models : []
          );
          content = retry.content || content;
          mdRaw = extractMarkdown(content);
        }
      }

      if (!mdRaw) {
        // Fall back immediately if model(s) returned nothing
        const theme = THEME_PRESETS[themeKey] || THEME_PRESETS["minimal"];
        const mdDeckFallback = buildFallbackDeckFromNotes(input, theme, format, density);
        onApply(mode, mdDeckFallback);
        onClose();
        toast({ title: "Fallback used", description: "AI returned no Markdown. Built a multi-slide deck from your notes." });
        setLoading(false);
        return;
      }

      const theme = THEME_PRESETS[themeKey] || THEME_PRESETS["minimal"];
      let mdDeck = normalizeDeck(mdRaw, theme);

      // If still only one slide, synthesize a multi-slide deck from notes
      const slideCount = mdDeck.replace(/\r\n/g, "\n").split(/\n-{3,}\n/).filter((s) => s.trim().length > 0).length;
      if (slideCount < 2) {
        mdDeck = buildFallbackDeckFromNotes(input, theme, format, density);
        toast({ title: "Fallback applied", description: "Model output lacked slide structure. Created a multi-slide deck from your notes." });
      }

      // NEW: auto-generate images for "Visual:" lines if enabled
      if (visualNotes && autoImages) {
        mdDeck = await injectGeneratedImages(mdDeck);
      }

      onApply(mode, mdDeck);
      onClose();
    } catch (e: any) {
      toast({ title: "Generation failed", description: e?.message || "Please try again.", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (!v ? onClose() : undefined)}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle>Generate slides (themes + formats)</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Controls row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="space-y-1">
              <Label htmlFor="format">Format</Label>
              <Select value={format} onValueChange={(v: FormatKey) => setFormat(v)}>
                <SelectTrigger id="format"><SelectValue placeholder="Choose a format" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(FORMAT_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="theme">Theme</Label>
              <Select value={themeKey} onValueChange={setThemeKey}>
                <SelectTrigger id="theme"><SelectValue placeholder="Choose a theme" /></SelectTrigger>
                <SelectContent>
                  {Object.entries(THEME_PRESETS).map(([k, t]) => (
                    <SelectItem key={k} value={k}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="density">Amount of text</Label>
              <Select value={density} onValueChange={(v: any) => setDensity(v)}>
                <SelectTrigger id="density"><SelectValue placeholder="Concise" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="minimal">Minimal</SelectItem>
                  <SelectItem value="concise">Concise</SelectItem>
                  <SelectItem value="detailed">Detailed</SelectItem>
                  <SelectItem value="extensive">Extensive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Toggles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
            <div className="flex items-center gap-2">
              <Switch id="two-col" checked={twoColumn} onCheckedChange={setTwoColumn} />
              <Label htmlFor="two-col">Emphasize two‑column slides</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="visuals" checked={visualNotes} onCheckedChange={setVisualNotes} />
              <Label htmlFor="visuals">Include "Visual:" suggestions</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="advanced" checked={advancedMode} onCheckedChange={setAdvancedMode} />
              <Label htmlFor="advanced">Advanced cleanup</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="auto-images" checked={autoImages} onCheckedChange={setAutoImages} />
              <Label htmlFor="auto-images">Auto‑generate images for "Visual:"</Label>
            </div>
          </div>

          {/* Audience & tone */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="audience">Write for</Label>
              <Input id="audience" value={audience} onChange={(e) => setAudience(e.target.value)} placeholder="e.g., executives, investors, policy makers" />
            </div>
            <div className="space-y-1">
              <Label htmlFor="tone">Tone</Label>
              <Input id="tone" value={tone} onChange={(e) => setTone(e.target.value)} placeholder="e.g., confident, concise, authoritative" />
            </div>
          </div>

          {/* Notes / outline */}
          <div>
            <Label className="text-xs text-muted-foreground">Paste notes, outline, or text</Label>
            <Textarea
              value={input}
              onChange={(e) => setInput(e.target.value)}
              className="mt-2 h-48 resize-none"
              placeholder="Type or paste content here… Slides will be generated in Markdown with --- separators, themed via ::bg and ::text."
            />
          </div>

          {/* Mode buttons */}
          <div className="grid grid-cols-2 gap-2">
            <Button size="sm" variant={mode === "replace" ? "secondary" : "outline"} onClick={() => setMode("replace")}>
              Replace deck
            </Button>
            <Button size="sm" variant={mode === "append" ? "secondary" : "outline"} onClick={() => setMode("append")}>
              Append to deck
            </Button>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <div className="text-xs text-muted-foreground">Output is pure Markdown. Slides separated by ---; theme directives (::bg, ::text) added per slide.</div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button disabled={loading} onClick={generate}>
              <Sparkles className="h-4 w-4 mr-1" />
              {loading ? "Generating…" : "Generate"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MapleSlideGenerator;